// src/components/NotificationsPage.js
import React, { useEffect, useState } from "react";
import axios from "../axios";
import BackLink from "./BackLink";
import "./NotificationsPage.css";
import { useNavigate } from "react-router-dom";
import { getNotificationMeta } from "./notificationTypes";
import { getUnreadChatCount } from "../api/chatApi";

export default function NotificationsPage({ onRequireAuth }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [isStandaloneApp, setIsStandaloneApp] = useState(false);

  useEffect(() => {
    window.dispatchEvent(new Event("refresh-unread-count"));

    axios.post("/api/notifications/mark-seen/").catch((e) => {
      console.warn("mark-seen failed:", e);
    });
  }, []);

  useEffect(() => {
    setIsStandaloneApp(true);
  }, []);

  useEffect(() => {
    const load = async () => {
      setError("");

      // если не авторизован — показываем аккуратный экран + кнопка входа
      if (!token) {
        setLoading(false);
        setChatUnreadCount(0);
        return;
      }

      try {
        setLoading(true);
        const [notifRes, chatRes] = await Promise.all([
          axios.get("/api/notifications/", {
            params: { limit: 50 },
          }),
          getUnreadChatCount(token),
        ]);

        setItems(Array.isArray(notifRes.data) ? notifRes.data : []);
        setChatUnreadCount(Number(chatRes?.unread_conversations ?? 0));
      } catch (e) {
        console.error("Σφάλμα φόρτωσης ειδοποιήσεων:", e);
        setError("❌ Σφάλμα φόρτωσης ειδοποιήσεων.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return "";
    }
  };

  const getActorName = (n) => {
    return (n?.actor?.name && n.actor.name.trim()) || "DEAL";
  };

  const getInitial = (name = "") => {
    return (name.trim() || "D").charAt(0).toUpperCase();
  };

  const getNotificationTitle = (n, meta) => {
    const count = Array.isArray(n.target_ad_ids)
      ? n.target_ad_ids.length
      : Array.isArray(n.ad_titles)
        ? n.ad_titles.length
        : 0;

    if (n.notif_type === "favorite_ad_price_changed") {
      return count > 1
        ? `Μειώθηκε η τιμή σε ${count} αποθηκευμένες αγγελίες:`
        : "Μειώθηκε η τιμή σε αποθηκευμένη αγγελία:";
    }

    if (n.notif_type === "new_ad") {
      if (count > 1) {
        return `δημοσίευσε ${count} νέες αγγελίες:`;
      }

      return n.text?.replace(getActorName(n), "").trim() || meta.title;
    }

    if (n.notif_type === "favorite_ad_completed") {
      return count > 1
        ? `${count} αποθηκευμένες αγγελίες ολοκληρώθηκαν:`
        : meta.title;
    }


    return meta.title;
  };

  const markRead = async (id) => {
    try {
      await axios.post(`/api/notifications/${id}/read/`);
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
    } catch (e) {
      console.warn("Δεν ήταν δυνατή η επισήμανση ως αναγνωσμένο:", e);
    }
  };

  const handleItemClick = async (n) => {
    // 1️⃣ помечаем прочитанным
    await markRead(n.id);
    window.dispatchEvent(new Event("refresh-unread-count"));

    const open = (url) => {
      if (isStandaloneApp) {
        navigate(url);
        return;
      }
      window.open(url, "_blank");
    };

    // ==============================
    // ❤️ ОБЪЯВЛЕНИЕ ИЗ ИЗБРАННОГО ЗАВЕРШЕНО (ПРОДАНО)
    // ведём в МОИ ОБЪЯВЛЕНИЯ → вкладка завершённые
    // ==============================
    if (n.notif_type === "favorite_ad_completed" && n.target_profile_id) {
      const ids = Array.isArray(n.target_ad_ids) ? n.target_ad_ids : [];
      const qs = ids.length
        ? `?tab=closed&highlightIds=${encodeURIComponent(ids.join(","))}`
        : "?tab=closed";

      open(`/seller/${n.target_profile_id}${qs}`);
      return;
    }

    // ==============================
    // 📉 СНИЖЕНИЕ ЦЕНЫ В ИЗБРАННОМ
    // ведём в профиль продавца, подсвечиваем объявление/объявления
    // ==============================
    if (n.notif_type === "favorite_ad_price_changed" && n.target_profile_id) {
      const ids = Array.isArray(n.target_ad_ids) ? n.target_ad_ids : [];

      const qs = ids.length
        ? `?highlight=priceChanged&adIds=${encodeURIComponent(ids.join(","))}`
        : "?highlight=priceChanged";

      open(`/seller/${n.target_profile_id}${qs}`);
      return;
    }


    // ==============================
    // 🆕 НОВЫЕ ОБЪЯВЛЕНИЯ ПРОДАВЦА (подписки)
    // ==============================
    if (n.notif_type === "new_ad" && n.target_profile_id) {
      const ids = Array.isArray(n.target_ad_ids) ? n.target_ad_ids : [];
      const qs = ids.length ? `&adIds=${encodeURIComponent(ids.join(","))}` : "";

      open(`/seller/${n.target_profile_id}?highlight=newAds${qs}`);
      return;
    }

    // ==============================
    // 💬 NEW MESSAGE
    // ==============================
    if (n.notif_type === "new_message" && n.target_conversation_id) {
      navigate(`/chat?conversation=${n.target_conversation_id}`);
      return;
    }

    // ==============================
    // 👤 ПРОФИЛЬ (fallback)
    // ==============================
    if (n.target_profile_id) {
      open(`/seller/${n.target_profile_id}`);
      return;
    }

    // ==============================
    // 📦 ОБЪЯВЛЕНИЕ (fallback)
    // ==============================
    if (n.target_ad_id) {
      open(`/ads/${n.target_ad_id}`);
      return;
    }

    console.warn("Notification χωρίς στόχο:", n);
  };

  if (!token) {
    return (
      <div className="notifications-page">
        <BackLink label="Πίσω" />
         <h1 className="notifications-title">Ειδοποιήσεις</h1>

        <div className="notifications-empty">
          Για να δείτε ειδοποιήσεις, πρέπει να συνδεθείτε.
        </div>

        <button
          className="green-button"
          onClick={() => onRequireAuth && onRequireAuth()}
          style={{ marginTop: 12 }}
        >
          Είσοδος ή Εγγραφή
        </button>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <BackLink label="Πίσω" />

      <div className="notifications-header-row">
        <h1 className="notifications-title">Ειδοποιήσεις</h1>

        {isStandaloneApp && (
          <button
            className="green-button notifications-chat-button"
            onClick={() => navigate("/chat")}
          >
            Όλες οι συνομιλίες

            {chatUnreadCount > 0 && (
              <span className="notifications-chat-badge">
                {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
              </span>
            )}
          </button>
        )}
      </div>

      {loading ? (
        <div className="notifications-empty">Φόρτωση...</div>
      ) : error ? (
        <div className="notifications-empty">{error}</div>
      ) : items.length === 0 ? (
        <div className="notifications-empty">Δεν υπάρχουν ειδοποιήσεις.</div>
      ) : (
        <div className="notifications-list">
          {items.map((n) => {
            const meta = getNotificationMeta(n.notif_type);

            return (
              <div
                key={n.id}
                onClick={(e) => {
                  if (e.target.closest(".notification-ad-item")) return;
                  handleItemClick(n);
                }}
                className={`notification-item ${n.is_read ? "is-read" : "is-unread"}`}
              >
                <div className="notification-row">

                  {/* 👤 АВАТАР */}
                  <div className="notification-avatar">
                    {n.actor?.avatar ? (
                      <img src={n.actor.avatar} alt="" />
                    ) : (
                      <div className="notification-avatarFallback">
                        {getInitial(getActorName(n))}
                      </div>
                    )}
                  </div>

                  {/* 🔔 ИКОНКА ТИПА УВЕДОМЛЕНИЯ */}
                  <div
                    className="notification-typeIcon"
                    style={{ background: meta.color + "20", color: meta.color }}
                  >
                    {meta.icon}
                  </div>

                  {/* 📄 CONTENT */}
                  <div className="notification-content">
                    <div className="notification-head">
                      {n.notif_type !== "favorite_ad_completed" && (
                        <span className="notification-actor">
                          {getActorName(n)}
                        </span>
                      )}

                      <span className="notification-action">
                        {getNotificationTitle(n, meta)}
                      </span>

                      {!n.is_read && <span className="notification-dot" />}
                    </div>

                    <div className="notification-body">
                      {(
                        n.notif_type === "new_ad" ||
                        n.notif_type === "favorite_ad_completed" ||
                        n.notif_type === "favorite_ad_price_changed"
                      ) && n.ad_titles?.length ? (
                        <div className="notification-ad-list">
                          {n.ad_titles.map((title, idx) => {
                            const ids = Array.isArray(n.target_ad_ids) ? n.target_ad_ids : [];
                            const adId = ids[idx];

                            return (
                              <div
                                key={idx}
                                className="notification-ad-item"
                                onClick={(e) => {
                                  e.stopPropagation();

                                  if (n.notif_type === "new_ad") {
                                    const qs = ids.length
                                      ? `&adIds=${encodeURIComponent(ids.join(","))}`
                                      : "";

                                    window.open(
                                      `/seller/${n.target_profile_id}?highlight=newAds${qs}&focusAd=${adId}`,
                                      "_blank"
                                    );
                                    return;
                                  }

                                  if (n.notif_type === "favorite_ad_completed") {
                                    const qs = ids.length
                                      ? `&highlightIds=${encodeURIComponent(ids.join(","))}`
                                      : "";

                                    window.open(
                                      `/seller/${n.target_profile_id}?tab=closed${qs}&focusAd=${adId}`,
                                      "_blank"
                                    );
                                  }
                                  if (n.notif_type === "favorite_ad_price_changed") {
                                    window.open(`/ads/${adId}`, "_blank", "noopener,noreferrer");
                                    return;
                                  }
                                }}
                              >
                                {title}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        n.notif_type === "new_message"
                            ? (n.message_preview || "Νέο μήνυμα")
                            : (n.text || "Ειδοποίηση")
                      )}
                    </div>

                    <div className="notification-meta">
                      {formatTime(n.created_at)}
                    </div>
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
